/* VELSENOR v24: one scheduler for cursor, visible depth scenes and product rails. */
(() => {
  'use strict';
  const ready = callback => document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', callback, {once: true}) : callback();
  ready(() => {
    const body = document.body;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const fine = matchMedia('(hover: hover) and (pointer: fine)');
    const forcedColors = matchMedia('(forced-colors: active)');
    const motionEnabled = body.classList.contains('motion-enabled');
    const canvas = document.querySelector('[data-lc-cursor-particles]');
    const context = canvas?.getContext('2d', {alpha: true, desynchronized: true});
    const atmosphere = document.querySelector('[data-lc-atmosphere]');
    const progress = document.querySelector('.page-progress span');
    let header = document.querySelector('[data-header]');
    const scenes = new Map();
    const rails = new Map();
    const size = 184;
    const center = size / 2;
    const count = canvas?.dataset.particles === 'false' ? 0 : 18;
    const intensity = Math.max(.6, Math.min(1.6, Number(body.dataset.scrollDepth || 130) / 100));
    const pointer = {x: -200, y: -200, active: false, hover: false, text: false, dirty: false, energy: 0, movedAt: 0};
    let frame = 0, idleTimer = 0, resizeTimer = 0, lastTime = 0;
    let scrollDirty = true, pointerDirty = false, activeScene = null, previousScene = null;
    let pageHeight = 1, viewportHeight = innerHeight, lastScroll = scrollY;
    let enabled = motionEnabled && !reduced.matches && !forcedColors.matches;
    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
    let glow = null;

    function configureCanvas() {
      if (!canvas || !context) return;
      // A small local surface replaces the former full-viewport high-DPI canvas.
      const dpr = Math.min(devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(size * dpr);
      canvas.height = Math.round(size * dpr);
      canvas.style.width = size + 'px';
      canvas.style.height = size + 'px';
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (canvas.dataset.glow !== 'false') {
        glow = context.createRadialGradient(center, center, 8, center, center, 68);
        glow.addColorStop(0, 'rgba(203,162,88,0)');
        glow.addColorStop(.38, 'rgba(203,162,88,.065)');
        glow.addColorStop(1, 'rgba(203,162,88,0)');
      }
    }

    function drawCursor(time, elapsed) {
      if (!context || !canvas) return;
      if (pointer.dirty) {
        // The visual uses the latest pointer position, without a trailing lerp.
        canvas.style.transform = `translate3d(${pointer.x - center}px,${pointer.y - center}px,0)`;
        pointer.dirty = false;
      }
      canvas.style.opacity = pointer.text ? '.22' : '1';
      context.clearRect(0, 0, size, size);
      if (glow) {context.fillStyle = glow; context.fillRect(0, 0, size, size);}
      pointer.energy *= Math.exp(-elapsed / 230);
      const energy = pointer.energy;
      const clock = time * .001;
      for (let i = 0; i < count; i++) {
        const plane = i % 3;
        const direction = plane === 1 ? -1 : 1;
        const angle = clock * (1.15 + plane * .38) * direction + i * 2.39996;
        const radius = 24 + plane * 11 + Math.sin(clock * 1.9 + i) * 4 + energy * 6 + (pointer.hover ? 3 : 0);
        const tilt = plane * 1.04 + Math.sin(clock * .36) * .3;
        const orbitX = Math.cos(angle) * radius;
        const orbitY = Math.sin(angle) * radius * (.43 + plane * .12);
        const x = center + orbitX * Math.cos(tilt) - orbitY * Math.sin(tilt);
        const y = center + orbitX * Math.sin(tilt) + orbitY * Math.cos(tilt);
        const dot = .65 + (i % 4) * .24 + energy * .16;
        const alpha = .4 + (Math.sin(angle) + 1) * .22;
        // Short tangent strokes give movement without a growing trail buffer.
        context.beginPath();
        context.moveTo(x, y);
        context.lineTo(x + Math.sin(angle + tilt) * (2 + energy * 3), y - Math.cos(angle + tilt) * (2 + energy * 3));
        context.lineWidth = .8;
        context.strokeStyle = `rgba(156,112,42,${alpha * .45})`;
        context.stroke();
        context.beginPath(); context.arc(x, y, dot + .6, 0, Math.PI * 2);
        context.fillStyle = `rgba(98,65,22,${alpha * .42})`; context.fill();
        context.beginPath(); context.arc(x, y, dot, 0, Math.PI * 2);
        context.fillStyle = i % 4 === 0 ? `rgba(255,239,195,${alpha})` : `rgba(207,165,83,${alpha})`;
        context.fill();
      }
    }

    function drawAtmosphere() {
      if (!atmosphere) return;
      const ctx = atmosphere.getContext('2d', {alpha: true});
      if (!ctx) return;
      atmosphere.width = innerWidth; atmosphere.height = innerHeight;
      ctx.clearRect(0, 0, innerWidth, innerHeight);
      if (!enabled || !fine.matches) return;
      // Background flecks are static; only the small cursor surface animates.
      for (let i = 0; i < 24; i++) {
        const x = ((i * .61803398875) % 1) * innerWidth;
        const y = ((i * .38196601125 + .17) % 1) * innerHeight;
        ctx.beginPath(); ctx.arc(x, y, .6 + i % 3 * .25, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(170,127,56,.35)'; ctx.fill();
      }
    }

    function stop() {
      cancelAnimationFrame(frame); clearTimeout(idleTimer);
      frame = 0; idleTimer = 0; lastTime = 0;
    }
    function wake() {
      clearTimeout(idleTimer); idleTimer = 0;
      if (!frame && !document.hidden) frame = requestAnimationFrame(render);
    }
    function measurePage() {
      viewportHeight = innerHeight;
      pageHeight = Math.max(1, document.documentElement.scrollHeight - viewportHeight);
      scrollDirty = true;
      scenes.forEach(state => {state.rect = null;});
      rails.forEach(state => {state.max = Math.max(0, state.rail.scrollWidth - state.rail.clientWidth);});
      wake();
    }
    function resetPointerScene(state) {
      if (state) state.layers.forEach(layer => layer.element.style.removeProperty('transform'));
    }
    function render(time) {
      frame = 0;
      if (document.hidden) return;
      const elapsed = lastTime ? Math.min(48, time - lastTime) : 16.67;
      lastTime = time;
      const top = scrollY;
      const pending = [];
      // Collect geometry before writing styles, and only for visible scenes.
      scenes.forEach(state => {
        if (!state.visible || (!scrollDirty && !(pointerDirty && state === activeScene))) return;
        if (scrollDirty || !state.rect) state.rect = state.scene.getBoundingClientRect();
        if (scrollDirty) pending.push(state);
      });
      if (scrollDirty) {
        header?.classList.toggle('is-scrolled', top > 24);
        if (progress) progress.style.transform = `scaleX(${clamp(top / pageHeight, 0, 1)})`;
        const speed = Math.min(1, Math.abs(top - lastScroll) / Math.max(16, elapsed) * .2);
        lastScroll = top;
        pending.forEach(state => {
          if (!enabled) return;
          const p = clamp((viewportHeight - state.rect.top) / (viewportHeight + state.rect.height), 0, 1);
          const phase = p - .5, focus = Math.sin(p * Math.PI), energy = focus * .58 + speed * .25;
          const style = state.scene.style;
          style.setProperty('--cinema-progress', p.toFixed(4));
          style.setProperty('--lc-depth-energy', energy.toFixed(3));
          style.setProperty('--lc-depth-grid-opacity', (.026 + energy * .06).toFixed(3));
          style.setProperty('--lc-depth-glow-opacity', (.045 + energy * .12).toFixed(3));
          style.setProperty('--lc-depth-shift', `${(phase * 42 * intensity).toFixed(2)}px`);
          style.setProperty('--lc-depth-tilt', `${(phase * -1.45 * intensity).toFixed(3)}deg`);
          style.setProperty('--lc-flow-position', `${(p * 100).toFixed(2)}%`);
          // Text and photographic media stay still and sharp; only decor moves.
          if (state.word) {state.word.style.translate = `${(phase * -3.8 * intensity).toFixed(3)}vw 0`; state.word.style.opacity = (.42 + focus * .4).toFixed(3);}
          if (state.portal) {state.portal.style.scale = (.88 + p * .23 * intensity).toFixed(4); state.portal.style.opacity = (.2 + focus * .56).toFixed(3);}
          if (state.constellation) {state.constellation.style.scale = (1 + phase * .095 * intensity).toFixed(4); state.constellation.style.opacity = (.3 + focus * .5).toFixed(3);}
          if (state.line) state.line.style.transform = `scaleY(${p.toFixed(4)})`;
        });
        scrollDirty = false;
      }
      if (pointerDirty) {
        if (previousScene !== activeScene) resetPointerScene(previousScene);
        if (enabled && fine.matches && activeScene?.visible && activeScene.rect) {
          const state = activeScene, rect = state.rect;
          const x = clamp((pointer.x - rect.left) / Math.max(1, rect.width) - .5, -.5, .5) * 2;
          const y = clamp((pointer.y - rect.top) / Math.max(1, rect.height) - .5, -.5, .5) * 2;
          state.layers.forEach(layer => {
            const d = layer.depth * intensity;
            layer.element.style.transform = `translate3d(${(x * d * 30).toFixed(2)}px,${(y * d * 22).toFixed(2)}px,${(d * 24).toFixed(2)}px) rotateX(${(-y * d * 2).toFixed(2)}deg) rotateY(${(x * d * 2.6).toFixed(2)}deg)`;
          });
        }
        previousScene = activeScene; pointerDirty = false;
      }
      let railMoving = false;
      rails.forEach(state => {
        if (!enabled || !state.visible || state.paused || state.hover || state.focused || state.max < 1) return;
        state.position += state.speed * elapsed;
        if (state.position >= state.max) state.position = 0;
        state.rail.scrollLeft = state.position;
        railMoving = true;
      });
      const cursorMoving = enabled && fine.matches && pointer.active && !!context;
      if (cursorMoving) drawCursor(time, elapsed);
      if (railMoving || (cursorMoving && time - pointer.movedAt < 140)) wake();
      else if (cursorMoving) idleTimer = setTimeout(wake, 1000 / 30);
      else lastTime = 0;
    }

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const scene = scenes.get(entry.target);
        if (scene) {
          scene.visible = entry.isIntersecting;
          scene.scene.classList.toggle('is-scene-active', scene.visible);
          scene.rect = null;
          if (!scene.visible) resetPointerScene(scene);
          else body.dataset.activeWorld = scene.scene.id.replace('scene-', '');
        }
        const rail = rails.get(entry.target);
        if (rail) rail.visible = entry.isIntersecting;
      });
      scrollDirty = true; wake();
    }, {rootMargin: '64px 0px', threshold: .01});

    function collect(scope, selector) {
      return [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
    }
    function scan(scope) {
      collect(scope, '[data-deep-scene]').forEach(scene => {
        if (scenes.has(scene)) return;
        scenes.set(scene, {scene, visible: false, rect: null,
          layers: [...scene.querySelectorAll('[data-depth-layer]')].filter(el => !el.matches('.deep-scene__copy,.deep-scene__media')).map(element => ({element, depth: Number(element.dataset.depthLayer || .1)})),
          word: scene.querySelector('.deep-scene__word'), portal: scene.querySelector('.deep-scene__portal'),
          constellation: scene.querySelector('.deep-scene__constellation'), line: scene.querySelector('.deep-scene__progress i')});
        observer.observe(scene);
      });
      collect(scope, '[data-auto-rail]').forEach(rail => {
        if (rails.has(rail)) return;
        const state = {rail, visible: false, paused: false, hover: false, focused: false, position: rail.scrollLeft,
          max: Math.max(0, rail.scrollWidth - rail.clientWidth), speed: Math.max(1, Number(rail.dataset.speed || 2)) * .0132};
        rails.set(rail, state); observer.observe(rail);
        const wrap = rail.closest('.auto-rail-wrap'), toggle = wrap?.querySelector('[data-rail-toggle]');
        const updateToggle = () => {
          if (!toggle) return;
          const paused = state.paused || !enabled;
          toggle.textContent = paused ? '▶' : 'Ⅱ';
          toggle.setAttribute('aria-label', paused ? (toggle.dataset.labelPlay || 'Play') : (toggle.dataset.labelPause || 'Pause'));
        };
        state.updateToggle = updateToggle;
        const pause = () => {state.paused = true; updateToggle();};
        rail.addEventListener('pointerenter', () => {state.hover = true;});
        rail.addEventListener('pointerleave', () => {state.hover = false; state.position = rail.scrollLeft; wake();});
        rail.addEventListener('focusin', () => {state.focused = true;});
        rail.addEventListener('focusout', event => {state.focused = rail.contains(event.relatedTarget); state.position = rail.scrollLeft; wake();});
        rail.addEventListener('pointerdown', pause, {passive: true});
        rail.addEventListener('wheel', pause, {passive: true});
        toggle?.addEventListener('click', () => {state.paused = !state.paused; state.position = rail.scrollLeft; updateToggle(); wake();});
        ['prev', 'next'].forEach(direction => wrap?.querySelector(`[data-rail-${direction}]`)?.addEventListener('click', () => {
          pause(); rail.scrollBy({left: (direction === 'prev' ? -1 : 1) * Math.max(300, rail.clientWidth * .7), behavior: reduced.matches ? 'auto' : 'smooth'});
        }));
        updateToggle();
      });
      measurePage();
    }

    function leave() {
      pointer.active = false; activeScene = null; pointerDirty = true;
      if (canvas) canvas.style.opacity = '0';
      wake();
    }
    document.addEventListener('pointermove', event => {
      if (!enabled || !fine.matches || event.pointerType === 'touch') return;
      const wasActive = pointer.active;
      if (wasActive) pointer.energy = Math.min(1.5, pointer.energy + Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) * .008);
      pointer.x = event.clientX; pointer.y = event.clientY; pointer.active = true;
      pointer.dirty = true; pointerDirty = true; pointer.movedAt = performance.now();
      wake();
    }, {passive: true});
    document.addEventListener('pointerover', event => {
      if (!enabled || !fine.matches || event.pointerType === 'touch') return;
      activeScene = scenes.get(event.target.closest?.('[data-deep-scene]')) || null;
      pointer.text = !!event.target.closest?.('input,textarea,[contenteditable="true"]');
      pointer.hover = !!event.target.closest?.('a,button,summary,[role="button"]');
      pointerDirty = true; wake();
    }, {passive: true});
    document.addEventListener('pointerdown', event => {
      if (event.button === 0 && pointer.active && enabled) {pointer.energy = 1.5; pointer.movedAt = performance.now(); wake();}
    }, {passive: true});
    document.documentElement.addEventListener('pointerleave', leave, {passive: true});
    addEventListener('blur', leave);
    addEventListener('scroll', () => {scrollDirty = true; pointerDirty = !!activeScene; wake();}, {passive: true});
    addEventListener('resize', () => {
      measurePage(); clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {configureCanvas(); drawAtmosphere(); pointer.dirty = true; wake();}, 120);
    }, {passive: true});
    addEventListener('load', measurePage, {once: true});
    document.fonts?.ready.then(measurePage);
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(measurePage) : null;
    resizeObserver?.observe(document.querySelector('main') || body);
    function preferences() {
      enabled = motionEnabled && !reduced.matches && !forcedColors.matches;
      body.dataset.motionPaused = String(document.hidden || !enabled);
      if (!enabled || !fine.matches) {leave(); scenes.forEach(resetPointerScene);}
      rails.forEach(state => state.updateToggle());
      drawAtmosphere(); scrollDirty = true; wake();
    }
    reduced.addEventListener('change', preferences);
    fine.addEventListener('change', preferences);
    forcedColors.addEventListener('change', preferences);
    document.addEventListener('visibilitychange', () => {
      body.dataset.motionPaused = String(document.hidden || !enabled);
      if (document.hidden) {pointer.active = false; if (canvas) canvas.style.opacity = '0'; stop();}
      else {scrollDirty = true; wake();}
    });
    document.addEventListener('shopify:section:load', event => {header = document.querySelector('[data-header]'); scan(event.target);});
    document.addEventListener('shopify:section:unload', event => {
      scenes.forEach((state, key) => {if (event.target.contains(key)) {observer.unobserve(key); scenes.delete(key); if (state === activeScene) leave();}});
      rails.forEach((state, key) => {if (event.target.contains(key)) {observer.unobserve(key); rails.delete(key);}});
      measurePage();
    });
    addEventListener('pagehide', stop);
    addEventListener('pageshow', () => {scrollDirty = true; wake();});
    configureCanvas(); scan(document); preferences();
  });
})();
